import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminProvider } from "./context/AdminContext";
import { DriverProvider } from "./context/DriverContext";
import { SearchProvider } from "./context/SearchContext";
import GlobalAlert from "./components/GlobalAlert";
import FullScreenLoader from "./components/FullScreenLoader";
import CookieConsent from "./components/CookieConsent";
/*import AnnouncementBar from "./components/AnnouncementBar";*/
import AOS from 'aos';
import 'aos/dist/aos.css';

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Product from "./pages/Product";
import Flowers from "./pages/Flowers";
import Accessories from "./pages/Accessories";
import Vip from "./pages/Vip";
import Build from "./pages/Build";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import NotFound from "./pages/NotFound";
import Navbar from "./components/Navbar";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Policies from "./pages/Policies";
import Terms from "./pages/Terms";
import FAQs from "./pages/FAQs";
import Blog from "./pages/Blog";
import SinglePost from "./pages/SinglePost";
import BackToTop from "./components/BackToTop";
import ScrollToTop from "./components/ScrollToTop";
import ProductModal from "./components/ProductModal";
import OrderConfirmation from "./pages/OrderConfirmation";
import './App.css';
import FloatingIcons from "./components/FloatingIcons";
import SignIn from "./auth/SignIn";
import Register from "./auth/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ResetSuccess from "./pages/ResetSuccess";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyNotice from "./auth/VerifyNotice";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";

// Driver pages
import DriverLayout from "./layouts/DriverLayout";
import DriverInterface from "./pages/DriverInterface";
import DriverLogin from "./pages/DriverLogin";
import DriverHistory from "./pages/DriverHistory";
import DriverProfile from "./pages/DriverProfile";
import ProtectedDriverRoute from "./routes/ProtectedDriverRoute";

// Admin portal
import AdminLayout from "./layouts/AdminLayout";
import ProtectedAdminRoute from "./routes/ProtectedAdminRoute";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/AdminOrders";
import ManageDrivers from "./pages/admin/ManageDrivers";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminSiteSettings from "./pages/admin/AdminSiteSettings";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminFinance from "./pages/admin/AdminFinance";
import AdminBlogManager from "./pages/admin/AdminBlogManager";
import AdminRolesManager from "./pages/admin/AdminRolesManager";
import CreateAdminAccount from "./pages/admin/CreateAdminAccount";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminUserManager from "./pages/admin/AdminUserManager";
import AdminOrderDetailsPage from "./pages/admin/AdminOrderDetailsPage";

// User dashboard
import TrackOrder from "./pages/TrackOrder";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import MyOrders from "./pages/dashboard/MyOrders";
import Settings from "./pages/dashboard/Settings";
import Wishlist from "./pages/dashboard/Wishlist";
import DashboardCart from "./pages/dashboard/DashboardCart";
import ProtectedUserRoute from "./routes/ProtectedUserRoute";

function AppWrapper() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const location = useLocation();
  const { loading } = useAuth();

  const isDriverPage = location.pathname.startsWith("/driver");
  const isAdminPage = location.pathname.startsWith("/admin");
  const isPublicPage = !isDriverPage && !isAdminPage;
  const hideUI = isDriverPage || isAdminPage;

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  if (loading) {
    return <FullScreenLoader />;
  }


  return (
    <>
      {/* {!hideUI && <AnnouncementBar />} */}
      {!hideUI && <Navbar />}
      {!hideUI && <BackToTop />}

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      <div className={`${hideUI ? "p-0" : ""}`}>
        {!hideUI && <FloatingIcons />}

        <Routes>
          {/* Public site */}
          <Route path="/" element={<Home setSelectedProduct={setSelectedProduct} />} />
          <Route path="/shop" element={<Shop setSelectedProduct={setSelectedProduct} />} />
          <Route path="/product/:id" element={<Product />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/build" element={<Build />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />  
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<SinglePost />} />
          <Route path="/flowers" element={<Flowers />} />
          <Route path="/accessories" element={<Accessories />} />
          <Route path="/vip" element={<Vip />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/reset-success" element={<ResetSuccess />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/verify-notice" element={<VerifyNotice />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          {/* Driver */}
          <Route path="/driver-login" element={<DriverLogin />} />

          <Route
            path="/driver"
            element={
              <ProtectedDriverRoute>
                <DriverLayout />
              </ProtectedDriverRoute>
            }
          >
            <Route index element={<DriverInterface />} />
            <Route path="interface" element={<DriverInterface />} />
            <Route path="history" element={<DriverHistory />} />
            <Route path="profile" element={<DriverProfile />} />
          </Route>

          {/* Admin */}
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin/orders/:orderId" element={<AdminOrderDetailsPage />} />
          <Route path="/admin" element={<ProtectedAdminRoute requiredPermission="dashboard"><AdminLayout /></ProtectedAdminRoute>}>
            <Route index element={<ProtectedAdminRoute requiredPermission="dashboard"><AdminDashboard /></ProtectedAdminRoute>} />
            <Route path="dashboard" element={<ProtectedAdminRoute requiredPermission="dashboard"><AdminDashboard /></ProtectedAdminRoute>} />
            <Route path="orders" element={<ProtectedAdminRoute requiredPermission="orders"><AdminOrders /></ProtectedAdminRoute>} />
            <Route path="drivers" element={<ProtectedAdminRoute requiredPermission="drivers"><ManageDrivers /></ProtectedAdminRoute>} />
            <Route path="site-settings" element={<ProtectedAdminRoute requiredPermission="site-settings"><AdminSiteSettings /></ProtectedAdminRoute>} />
            <Route path="products" element={<ProtectedAdminRoute requiredPermission="products"><AdminProducts /></ProtectedAdminRoute>} />
            <Route path="blogs" element={<ProtectedAdminRoute requiredPermission="blogs"><AdminBlogManager /></ProtectedAdminRoute>} />
            <Route path="roles" element={<ProtectedAdminRoute requiredPermission="site-settings"><AdminRolesManager /></ProtectedAdminRoute>} />
            <Route path="create" element={<ProtectedAdminRoute requiredPermission="site-settings"><CreateAdminAccount /></ProtectedAdminRoute>} />
            <Route path="finance" element={<ProtectedAdminRoute requiredPermission="finance"><AdminFinance /></ProtectedAdminRoute>} />
            <Route path="profile" element={<ProtectedAdminRoute requiredPermission="profile"><AdminProfile /></ProtectedAdminRoute>} />
            <Route path="users" element={<ProtectedAdminRoute requiredPermission="users"><AdminUserManager /></ProtectedAdminRoute>} />
          </Route>

          {/* User Dashboard */}
          <Route path="/dashboard" element={<ProtectedUserRoute><DashboardLayout /></ProtectedUserRoute>}>
            <Route index element={<DashboardHome />} />
            <Route path="orders" element={<MyOrders />} />
            <Route path="settings" element={<Settings />} />
            <Route path="wishlist" element={<Wishlist />} />
            <Route path="cart" element={<DashboardCart />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>

      {isPublicPage && <CookieConsent />}

    </>
  );
}

function App() {
  return (
    <Router>
      <AdminProvider>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <DriverProvider>
                <SearchProvider>
                  <ScrollToTop />
                  <AppWrapper />
                  <GlobalAlert />
                </SearchProvider>
              </DriverProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </AdminProvider>
    </Router>
  );
}


export default App;
